import {
  // Polaris React holdouts — there is no s-skeleton-* web component, and the
  // route this stands in for already uses exactly these three (see below).
  SkeletonPage,
  SkeletonBodyText,
  SkeletonDisplayText
} from "@shopify/polaris";

import { useAdminLoading } from "../utils/adminLoading";

/**
 * The loading picture for /analytics/campaigns/:campaignId
 * (components/Analytics/CampaignAnalyticsPage.jsx).
 *
 * That route previously fell back to RouteLoader's large centred spinner while
 * its chunk downloaded, then immediately drew its OWN skeleton once the chunk
 * arrived — spinner → skeleton → page, two pictures for one wait. This is the
 * defect PageSkeleton.jsx documents, on a third route.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS ONE IS NOT BUILT ON <PageSkeleton>, AND NOT ON <s-page>
 * ─────────────────────────────────────────────────────────────────────────────
 * This route has TWO gates in a row — the chunk download, then the campaign
 * query — and the shape was originally inherited from an inline Polaris React
 * `<SkeletonPage>` that CampaignAnalyticsPage.jsx drew for the second one. That
 * shape stands in for the badges/controls row (:427-465) and the four
 * key-metric tiles (:488-540), so it is kept.
 *
 * ⚠️ THE DUPLICATION IS NOW GONE, AND THAT IS THE POINT. CampaignAnalyticsPage's
 * `if (loading)` branch renders THIS COMPONENT (its inline `SkeletonPage` was
 * removed), so both gates draw one identical frame and there is nothing to drift
 * against. Previously they were two hand-maintained copies of one picture — the
 * exact hazard PageSkeleton.jsx documents.
 *
 * Consequence for anyone changing this file: you are changing BOTH gates at
 * once, which is what makes the handoff seamless. Drawing an `<s-page>` here
 * instead would look closer to the eventual real page, but only if the query
 * gate changed with it — otherwise it reintroduces a jump in the middle of one
 * wait.
 *
 * ⚠️ The real page's heading is the CAMPAIGN NAME (:408,
 * `campaign?.name || 'Campaign Analytics'`), which cannot be known before the
 * query resolves. "Campaign Analytics" is the route's own fallback and is what
 * both its loading (:371) and error (:395) states show, so it is the only
 * honest title available at this gate.
 *
 * Nothing below the 4-up metric grid is drawn: the daily-performance chart
 * (:543) is conditional on `chartData.length > 0` — data that does not exist
 * yet — and everything after it is off-screen on first paint.
 */
export default function CampaignAnalyticsSkeleton() {
  // Not rendering <PageSkeleton>, so this component owns the admin header
  // progress bar for its own lifetime. See utils/adminLoading.js.
  useAdminLoading();

  return (
    <SkeletonPage
      title="Campaign Analytics"
      backAction={{ content: "Analytics", url: "/analytics" }}
    >
      <s-stack direction="block" gap="base">
        {/* Status/type badges + data-mode toggle + date range pills */}
        <s-section>
          <s-stack direction="block" gap="base">
            <SkeletonDisplayText size="small" />
            <SkeletonBodyText lines={4} />
          </s-stack>
        </s-section>

        {/* Key metrics: Impressions · Conversion Rate · Conversions · Revenue */}
        <s-grid
          gridTemplateColumns="repeat(auto-fit, minmax(180px, 1fr))"
          gap="base"
        >
          <s-section>
            <SkeletonBodyText lines={2} />
          </s-section>
          <s-section>
            <SkeletonBodyText lines={2} />
          </s-section>
          <s-section>
            <SkeletonBodyText lines={2} />
          </s-section>
          <s-section>
            <SkeletonBodyText lines={2} />
          </s-section>
        </s-grid>
      </s-stack>
    </SkeletonPage>
  );
}
